import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LBtn, LCheckbox, Loader } from "@components";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApi } from "@services/useApi";
import { isEmpty } from "lodash";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { bool, object, SchemaOf, number } from "yup";
import { NavItems } from "../../../common/helpers/navigation";
import { SignupContainer } from "../components/SignupContainer";
import { Agreement as AgreementForm, updateAgreement } from "../store/agreementSlice";
import { useLocalStorage, LocalStorageKey } from "@helpers/localStorageHelper";
import { AssessmentBasket } from "../../purchase/pages/checkout";
import { Agreement, AgreementType, RoleId } from "@store/generalTypes";

const formSchema: SchemaOf<AgreementForm> = object({
    agreed: bool().required().oneOf([true], "You must check the box that you agree to the terms and conditions"),
    stamp: number().optional()
});

export const SignupTerms = (): JSX.Element => {
    const [agreementFile, setAgreementFile] = useState<Agreement | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [assessmentBasket, setAssessmentBasket] = useLocalStorage<AssessmentBasket>(LocalStorageKey.AssessmentBasket);
    const isUpdateAccount = state?.updateAccountInfo && !isEmpty(state.updateAccountInfo);
    const updateAccountInfo = state?.updateAccountInfo ?? undefined;

    const organizationId = state?.organizationId;
    const locationId = state?.locationId;
    const roleId = state?.roleId;
    const orgAdmin = state?.orgAdmin;
    const userId = state?.userId;

    const isFromInvite = organizationId && locationId && roleId;

    const {
        createOrganization,
        updateUser,
        updateLocationRole,
        getAgreements,
        createUserAgreement,
        loading: apiLoading
    } = useApi();

    const validateMethods = useForm<AgreementForm>({
        resolver: yupResolver(formSchema)
    });

    const user = useAppSelector((x) => x.user);
    const organization = useAppSelector((x) => x.organization);

    useEffect(() => {
        void loadAgreements();
    }, []);

    const loadAgreements = async (): Promise<void> => {
        const files = await getAgreements();
        let agreement: Agreement | null = null;

        if (orgAdmin) {
            agreement = files.find((x) => x.agreementType === AgreementType.ProviderAgreement)!;
        } else {
            agreement = files.find((x) => x.agreementType === AgreementType.EndUserLicenseAgreement)!;
        }

        setAgreementFile(agreement);
        setIsLoading(false);
    };

    const formSubmitHandler: SubmitHandler<AgreementForm> = async (data: AgreementForm) => {
        if (isUpdateAccount) {
            await updateUser(state?.email, updateAccountInfo);
            await updateLocationRole(state?.email, organizationId, locationId, roleId);
            await createUserAgreement(agreementFile!.id, userId, locationId, roleId);

            navigate(NavItems.Login.Landing.path);
        } else if (isFromInvite) {
            await updateLocationRole(state?.email, organizationId, locationId, roleId);
            await createUserAgreement(agreementFile!.id, userId, locationId, roleId);

            navigate(NavItems.Login.Landing.path);
        } else {
            dispatch(updateAgreement(data.agreed));

            const newOrg = await createOrganization({ ...organization, userId: user.id });

            await createUserAgreement(agreementFile!.id, user.id!, newOrg.locationId!, RoleId.Admin);

            setAssessmentBasket({ ...assessmentBasket, organizationId: newOrg.id! });

            navigate(NavItems.Signup.UserCreated.path);
        }
    };

    return (
        <FormProvider {...validateMethods}>
            <SignupContainer title={isUpdateAccount ? "Terms and Conditions" : "Review Terms and Conditions"}>
                {isLoading ? (
                    <Loader />
                ) : (
                    <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                        <section className="pb-2">
                            <iframe
                                style={{ flex: 1, border: "none", height: "41rem", width: "100%" }}
                                height="100%"
                                src={agreementFile!.documentUrl + "#toolbar=0&navpanes=0&view=fitH,fitV"}
                                title="Terms and Conditions"
                                allow="web-share"
                            />
                        </section>

                        <LCheckbox inputName="agreed" label="I have read and agree to the terms and conditions." />

                        <LBtn classes="mt-4" type="submit" fullWidth={true} loading={apiLoading}>
                            {isUpdateAccount ? "Click to Login and Create Account" : "Complete sign-up"}
                        </LBtn>
                    </form>
                )}
            </SignupContainer>
        </FormProvider>
    );
};
